import { Tag } from "antd-v5";
import User from "components/User";
import dayjs from "dayjs";
import { capitalize, isEmpty } from "lodash";
import React, { useMemo } from "react";
import { AssetEsDto } from "types/assetdetails";
import { formatArrayField } from "utils/assetUtils";
import { renderBooleanValues } from "views/Findings/FindingsListV3";
import CorrelationBasisPopover from "views/Inventory/Assets/AssetDetails/CorrelationBasisPopover";
import {
  CORRELATION_TAG_ICONS,
  getCorrelationRuleTags,
} from "views/Inventory/Assets/AssetDetails/correlationUtils";
import { ASSET_TYPE_FIELD_DISPLAY_MAP } from "views/Inventory/Assets/constants";

import { ACTableColumnType } from "ac-components/ACTable/ACTable";
import { NOT_AVAILABLE } from "ac-components/ac-na-component/ac-na-component";
import ACResponsiveTags from "ac-components/ac-responsive-tags/ACResponsiveTags";
import ACTag from "ac-components/ac-tag/ACTag";

import { DateFormatStrings } from "./useDateFormats";

// Re-export for backwards compatibility
export { getCorrelationRuleTags } from "views/Inventory/Assets/AssetDetails/correlationUtils";
export type { CorrelationTagInfo } from "views/Inventory/Assets/AssetDetails/correlationUtils";

// Helper to safely render array fields - ensures value is an array before passing to formatArrayField
const safeFormatArrayField = (value: string[] | string | undefined | null) => {
  if (!value) return NOT_AVAILABLE;
  if (Array.isArray(value)) return formatArrayField(value);
  return formatArrayField([value]);
};

// Helper to render string or array fields - returns raw string if not array
const renderStringOrArrayField = (value: string | string[] | null) => {
  if (!value) return NOT_AVAILABLE;
  if (Array.isArray(value)) return formatArrayField(value);
  return value;
};

// Default visible columns in correlated assets table
export const DEFAULT_CORRELATED_ASSET_COLUMN_KEYS: React.Key[] = [
  "name",
  "hostname",
  "armorCodeType",
  "source",
  "correlationBasis",
  "location",
];

// Column keys to exclude (Correlated Asset Count is not relevant for sourceMap assets)
const EXCLUDED_COLUMN_KEYS: React.Key[] = ["mergedAssetsCount"];

type CorrelatedAssetColumns = ACTableColumnType<AssetEsDto>[];

export interface UseCorrelatedAssetColumnsProps {
  sourceMap: AssetEsDto[];
}

export interface UseCorrelatedAssetColumnsResult {
  columns: CorrelatedAssetColumns;
  defaultHiddenKeys: React.Key[];
}

export default function useCorrelatedAssetColumns({
  sourceMap,
}: UseCorrelatedAssetColumnsProps): UseCorrelatedAssetColumnsResult {
  const columns = useMemo<CorrelatedAssetColumns>(() => {
    return [
      // Default visible columns
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        width: 180,
        ellipsis: true,
        render: (name: string | null) => name || NOT_AVAILABLE,
      },
      {
        title: "Hostname",
        dataIndex: "hostname",
        key: "hostname",
        width: 150,
        ellipsis: true,
        render: safeFormatArrayField,
      },
      {
        title: "Type",
        dataIndex: "armorCodeType",
        key: "armorCodeType",
        width: 120,
        render: (armorCodeType) => (
          <ACTag
            label={
              ASSET_TYPE_FIELD_DISPLAY_MAP[armorCodeType] ??
              capitalize(armorCodeType)
            }
            mode="label"
          />
        ),
      },
      {
        title: "Source",
        dataIndex: "source",
        key: "source",
        width: 150,
        ellipsis: true,
        render: renderStringOrArrayField,
      },
      {
        title: "Correlation Basis",
        dataIndex: ["assetCorrelationContext", "rule"],
        key: "correlationBasis",
        width: 300,
        render: (rule: string | null, correlatedAsset: AssetEsDto) => {
          const tags = getCorrelationRuleTags(rule);
          if (!tags) {
            return NOT_AVAILABLE;
          }
          return (
            <CorrelationBasisPopover
              rule={rule}
              sourceMap={sourceMap}
              correlatedAsset={correlatedAsset}
            >
              <span className="d-flex gap-1">
                {tags.map((tag, index) => (
                  <Tag
                    key={index}
                    color={tag.color}
                    icon={CORRELATION_TAG_ICONS[tag.iconType]}
                  >
                    {tag.label}
                  </Tag>
                ))}
              </span>
            </CorrelationBasisPopover>
          );
        },
      },
      {
        title: "Location",
        dataIndex: "location",
        key: "location",
        width: 150,
        ellipsis: true,
        render: safeFormatArrayField,
      },
      // Optional columns (hidden by default)
      {
        title: "Tool Asset Type",
        dataIndex: "toolType",
        key: "toolType",
        width: 120,
        render: (value: string | null) => value || NOT_AVAILABLE,
      },
      {
        title: "ID",
        dataIndex: "assetIdFromTool",
        key: "assetIdFromTool",
        width: 200,
        ellipsis: true,
        render: (value: string | null) => value || NOT_AVAILABLE,
      },
      {
        title: "First Seen",
        dataIndex: "firstSeen",
        key: "firstSeen",
        width: 150,
        render: (value: number | null) =>
          value
            ? dayjs(value).format(DateFormatStrings.FULL_DATE)
            : NOT_AVAILABLE,
      },
      {
        title: "OS",
        dataIndex: "os",
        key: "os",
        width: 200,
        render: safeFormatArrayField,
      },
      {
        title: "Asset Owner",
        dataIndex: "owner",
        key: "owner",
        width: 170,
        render: (owner) => {
          if (owner) {
            return <User withIcon userEmail={owner} />;
          }
          return NOT_AVAILABLE;
        },
      },
      {
        title: "DNS Name",
        dataIndex: "dnsName",
        key: "dnsName",
        width: 170,
        render: safeFormatArrayField,
      },
      {
        title: "OS Version",
        dataIndex: "osVersion",
        key: "osVersion",
        width: 170,
        render: renderStringOrArrayField,
      },
      {
        title: "Cloud Provider",
        dataIndex: "cloudProvider",
        key: "cloudProvider",
        width: 170,
        render: renderStringOrArrayField,
      },
      {
        title: "Cloud Account ID",
        dataIndex: "cloudAccountId",
        key: "cloudAccountId",
        width: 170,
        render: safeFormatArrayField,
      },
      {
        title: "ARN",
        dataIndex: "arn",
        key: "arn",
        width: 200,
        ellipsis: true,
        render: safeFormatArrayField,
      },
      {
        title: "MAC Address",
        dataIndex: "macAddress",
        key: "macAddress",
        width: 170,
        render: safeFormatArrayField,
      },
      {
        title: "Image Repo",
        dataIndex: "imageRepo",
        key: "imageRepo",
        width: 170,
        render: (value) => value || NOT_AVAILABLE,
      },
      {
        title: "Tags",
        dataIndex: "tags",
        key: "tags",
        width: 120,
        render: (tags: string[] | undefined) => {
          if (tags && Array.isArray(tags) && tags.length > 0) {
            return (
              <ACResponsiveTags
                tags={tags}
                secondaryLabelDelimiter=":"
                secondaryTitle="Value"
                columnTitle="Key"
              />
            );
          }
          return NOT_AVAILABLE;
        },
      },
      {
        title: "Registry",
        dataIndex: "registry",
        key: "registry",
        width: 170,
        render: (value) => value || NOT_AVAILABLE,
      },
      {
        title: "Cluster",
        dataIndex: "cluster",
        key: "cluster",
        width: 170,
        render: (value) => value || NOT_AVAILABLE,
      },
      {
        title: "Namespace",
        dataIndex: "namespace",
        key: "namespace",
        width: 170,
        render: (value) => value || NOT_AVAILABLE,
      },
      {
        title: "Code Repo",
        dataIndex: "codeRepo",
        key: "codeRepo",
        width: 170,
        render: (value) => value || NOT_AVAILABLE,
      },
      {
        title: "Cloud Resource",
        dataIndex: ["cloudResourceProperties", "cloudService"],
        key: "cloudService",
        width: 170,
        render: (value: string[] | null) => safeFormatArrayField(value),
      },
      {
        title: "Publicly Accessible",
        dataIndex: ["cloudResourceProperties", "publiclyAccessible"],
        key: "publiclyAccessible",
        width: 170,
        render: (value: boolean | null) =>
          value !== null ? renderBooleanValues(value) : NOT_AVAILABLE,
      },
      {
        title: "Region",
        dataIndex: ["cloudResourceProperties", "region"],
        key: "region",
        width: 170,
        render: (value: string[] | null) => safeFormatArrayField(value),
      },
      {
        title: "Runtime",
        dataIndex: ["cloudResourceProperties", "runtime"],
        key: "runtime",
        width: 170,
        render: (value: string[] | null) => safeFormatArrayField(value),
      },
      {
        title: "Architecture",
        dataIndex: ["cloudResourceProperties", "architecture"],
        key: "architecture",
        width: 170,
        render: (value: string[] | null) => safeFormatArrayField(value),
      },
      {
        title: "VPC ID",
        dataIndex: ["cloudResourceProperties", "vpcId"],
        key: "vpcId",
        width: 170,
        render: (value: string[] | null) => safeFormatArrayField(value),
      },
      {
        title: "Role",
        dataIndex: ["cloudResourceProperties", "role"],
        key: "role",
        width: 170,
        render: (value: string[] | null) => safeFormatArrayField(value),
      },
      {
        title: "Version",
        dataIndex: ["cloudResourceProperties", "version"],
        key: "version",
        width: 170,
        render: (value: string[] | null) => safeFormatArrayField(value),
      },
      {
        title: "Storage Type",
        dataIndex: ["cloudResourceProperties", "storageType"],
        key: "storageType",
        width: 170,
        render: (value: string[] | null) => safeFormatArrayField(value),
      },
      {
        title: "Engine Version",
        dataIndex: ["cloudResourceProperties", "engineVersion"],
        key: "engineVersion",
        width: 170,
        render: (value: string[] | null) => safeFormatArrayField(value),
      },
      {
        title: "Engine",
        dataIndex: ["cloudResourceProperties", "engine"],
        key: "engine",
        width: 170,
        render: (value: string[] | null) => safeFormatArrayField(value),
      },
      {
        title: "Instance Status",
        dataIndex: ["cloudResourceProperties", "instanceStatus"],
        key: "instanceStatus",
        width: 170,
        render: (value: string[] | null) => safeFormatArrayField(value),
      },
      {
        title: "Subnet IDs",
        dataIndex: ["cloudResourceProperties", "subnetIds"],
        key: "subnetIds",
        width: 170,
        render: (value: string[] | null) => safeFormatArrayField(value),
      },
      {
        title: "Image Names",
        dataIndex: ["cloudResourceProperties", "imageNames"],
        key: "imageNames",
        width: 170,
        render: (value: string[] | null) => safeFormatArrayField(value),
      },
      {
        title: "Native Types",
        dataIndex: ["cloudResourceProperties", "nativeTypes"],
        key: "nativeTypes",
        width: 170,
        render: (value: string[] | null) => safeFormatArrayField(value),
      },
      {
        title: "Kubernetes Flavor",
        dataIndex: ["cloudResourceProperties", "kubernetesFlavor"],
        key: "kubernetesFlavor",
        width: 170,
        render: (value: string[] | null) => safeFormatArrayField(value),
      },
      {
        title: "Authentication Mode",
        dataIndex: ["cloudResourceProperties", "authenticationMode"],
        key: "authenticationMode",
        width: 170,
        render: (value: string[] | null) => safeFormatArrayField(value),
      },
      {
        title: "Image Tag",
        dataIndex: "imageTag",
        key: "imageTag",
        width: 170,
        render: (value) => value || NOT_AVAILABLE,
      },
      {
        title: "IPV4",
        dataIndex: "ipv4",
        key: "ipv4",
        width: 170,
        render: (value: string[] | null) =>
          !isEmpty(value) ? value!.join(", ") : NOT_AVAILABLE,
      },
      {
        title: "IPV6",
        dataIndex: "ipv6",
        key: "ipv6",
        width: 170,
        render: (value: string[] | null) =>
          !isEmpty(value) ? value!.join(", ") : NOT_AVAILABLE,
      },
    ];
  }, [sourceMap]);

  // Calculate default hidden keys (all columns except the 6 default ones)
  const defaultHiddenKeys = useMemo(() => {
    return columns
      .filter(
        (col) =>
          !DEFAULT_CORRELATED_ASSET_COLUMN_KEYS.includes(col.key!) &&
          !EXCLUDED_COLUMN_KEYS.includes(col.key!),
      )
      .map((col) => col.key!);
  }, [columns]);

  return {
    columns,
    defaultHiddenKeys,
  };
}
